import {
  Button,
  CardContent,
  Grid,
  TextField,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HOST_URL from "../../../utils/Url";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";

type Props = {};

const StateMasterAdd = (props: Props) => {
  let navigate = useNavigate();
  const { t } = useTranslation();
  const [toaster, setToaster] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [option, setOption] = useState([
    { value: "-1", label: t("text.SelectCountryName") },
  ]);

  useEffect(() => {
    getCountryName();
  }, [])

  const getCountryName = () => {
    const collectData = {
      "country_id": -1,
      "user_id": "",
      "inst_ID": -1
    };
    axios
      .post(HOST_URL.HOST_URL2 + `Country/GetCountryMaster`, collectData)
      .then((res) => {
        const arr = [];
        for (let index = 0; index < res.data.data.length; index++) {
          arr.push({
            label: res.data.data[index]["country_name"],
            value: res.data.data[index]["country_id"],
          });
        }
        setOption(arr);
      });
  };

  // Enhanced validation schema with comprehensive input validation
  const validationSchema = Yup.object({
    country_id: Yup.string()
      .test(
        'required',
        t('text.reqcountryName'),
        function (value: any) {
          return value && value.trim() !== '';
        })
      .test(
        'valid-country',
        t('text.invalidCountrySelection'),
        function (value: any) {
          return value !== "-1" && value !== "" && value !== null;
        }
      ),
    state_name: Yup.string()
      .test(
        'required',
        t('text.reqstateName'),
        function (value: any) {
          return value && value.trim() !== '';
        }
      )
      .max(20, t('text.stateNameMaxLength'))
      .matches(/^[a-zA-Z\s\-]+$/, t('text.invalidStateName')) // Only letters, spaces, and hyphens
      .test(
        'no-special-chars-start-end',
        t('text.noSpecialCharsStartEnd'),
        function (value: any) {
          if (!value) return true;
          // Check if starts or ends with special characters (excluding spaces)
          return !/^[^a-zA-Z\s]|[^a-zA-Z\s]$/.test(value);
        }
      )
      .test(
        'no-multiple-special-chars',
        t('text.noMultipleSpecialChars'),
        function (value: any) {
          if (!value) return true;
          // Prevent multiple consecutive special characters
          return !/[\-\s]{2,}/.test(value);
        }
      ),
    state_shortname: Yup.string()
      .max(10, t('text.stateShortNameMaxLength'))
      .matches(/^[a-zA-Z\-]*$/, t('text.invalidStateShortName')) // Only letters and hyphens
      .test(
        'no-special-chars-start-end-short',
        t('text.noSpecialCharsStartEndShort'),
        function (value: any) {
          if (!value) return true;
          // Check if starts or ends with special characters
          return !/^[^a-zA-Z]|[^a-zA-Z]$/.test(value);
        }
      ),
  });

  // Function to sanitize input by removing unwanted characters
  const sanitizeInput = (value: string, fieldName: string) => {
    if (!value) return value;
    
    let sanitized = value;
    
    switch(fieldName) {
      case 'state_name':
        // Allow only letters, spaces, and hyphens
        sanitized = value.replace(/[^a-zA-Z\s\-]/g, '');
        // Remove multiple consecutive special characters
        sanitized = sanitized.replace(/([\-\s]){2,}/g, '$1');
        // Trim special characters from start and end
        sanitized = sanitized.replace(/^[^a-zA-Z\s]+|[^a-zA-Z\s]+$/g, '');
        break;
      case 'state_shortname':
        // Allow only letters and hyphens
        sanitized = value.replace(/[^a-zA-Z\-]/g, '');
        // Trim special characters from start and end
        sanitized = sanitized.replace(/^[^a-zA-Z]+|[^a-zA-Z]+$/g, '');
        break;
      default:
        // Default sanitization for other fields
        sanitized = value.replace(/[<>{}[\]\\]/g, '');
    }
    
    return sanitized;
  };

  // Custom onChange handler to sanitize input in real-time
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value, name);
    
    // Only update if the value changed after sanitization
    if (value !== sanitizedValue) {
      formik.setFieldValue(name, sanitizedValue);
    } else {
      formik.handleChange(e);
    }
  };

  const formik = useFormik({
    initialValues: {
      state_id: -1,
      state_name: "",
      state_shortname: "",
      country_id: 0,
      countryName: "",
      user_id: "",
      inst_ID: 0,
      allowedto: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Additional server-side validation before submission
      const sanitizedValues = {
        ...values,
        state_name: sanitizeInput(values.state_name, 'state_name'),
        state_shortname: sanitizeInput(values.state_shortname, 'state_shortname')
      };
      
      // Clear any existing timeout
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }

      // If already submitting, show message and return
      if (isSubmitting) {
        toast.info(t("text.requestInProgress"));
        return;
      }

      setIsSubmitting(true);

      try {
        const response = await axios.post(
          HOST_URL.HOST_URL2 + `State/AddUpdateStateMaster`,
          sanitizedValues
        );

        if (response.data.isSuccess) {
          setToaster(false);
          toast.success(response.data.mesg);
          navigate('/master/StateMaster');
        } else {
          setToaster(true);
          toast.error(response.data.mesg);
        }
      } catch (error: any) {
        if (error.response && error.response.status === 429) {
          toast.error(t("text.tooManyRequests"));
        } else {
          toast.error(t("text.somethingWentWrong"));
        }
        setToaster(true);
      } finally {
        // Set a timeout to reset isSubmitting after 2 seconds
        submitTimeoutRef.current = setTimeout(() => {
          setIsSubmitting(false);
        }, 2000);
      }
    },
  });

  const requiredFields = ['state_name', "country_id"];
  
  // Cleanup effect
  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  const back = useNavigate();
  
  return (
    <div>
      <div
        style={{
          padding: "-5px 5px",
          backgroundColor: "#ffffff",
          borderRadius: "5px",
          marginTop: "5px",
          border: ".5px solid #FF7722",
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            textAlign="center"
            style={{ fontSize: "18px", fontWeight: 500 }}
          >
            {t("text.CreateStateMaster")}
          </Typography>

          <Grid item sm={4} xs={12}>
            <Typography style={{ marginTop: "-75px" }}>
              <Button
                type="button"
                onClick={() => back(-1)}
                variant="contained"
                style={{
                  marginBottom: 15,
                  marginTop: "45px",
                  backgroundColor: `var(--header-background)`,
                  width: 20,
                }}
                disabled={isSubmitting}
              >
                <ArrowBackSharpIcon />
              </Button>
            </Typography>
          </Grid>
          <Divider />
          <br />
          <form onSubmit={formik.handleSubmit}>
            {toaster === false ? "" : <ToastApp />}
            <Grid item xs={12} container spacing={2}>
              <Grid xs={12} sm={4} item>
                <Autocomplete
                  disablePortal
                  id="country-autocomplete"
                  options={option}
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  onChange={(event, newValue: any) => {
                    formik.setFieldValue("country_id", newValue?.value);
                    formik.setFieldTouched("country_id", true);
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={
                        <span>
                          {t("text.SelectCountryName")} {""}
                          {requiredFields.includes('country_id') && (
                            <span style={{ color: formik.values.country_id ? 'green' : 'red' }}>*</span>
                          )}
                        </span>
                      }
                      error={formik.touched.country_id && Boolean(formik.errors.country_id)}
                    />
                  )}
                />

                {formik.touched.country_id && formik.errors.country_id ? (
                  <div style={{ color: "red", margin: "5px" }}>
                    {formik.errors.country_id}
                  </div>
                ) : null}
              </Grid>

              <Grid md={4} item>
                <TextField
                  label={
                    <span>
                      {t("text.EnterStateName")} {requiredFields.includes('state_name') && (
                        <span style={{ color: formik.values.state_name ? 'green' : 'red' }}>*</span>
                      )}
                    </span>
                  }
                  value={formik.values.state_name}
                  name="state_name"
                  id="state_name"
                  placeholder={t("text.EnterStateName")}
                  size="small"
                  fullWidth
                  style={{ backgroundColor: "white" }}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  disabled={isSubmitting}
                  inputProps={{ 
                    maxLength: 20,
                    pattern: "[a-zA-Z\\s\\-]*"
                  }}
                  error={formik.touched.state_name && Boolean(formik.errors.state_name)}
                />
                {formik.touched.state_name && formik.errors.state_name ? (
                  <div style={{ color: "red", margin: "5px" }}>
                    {formik.errors.state_name}
                  </div>
                ) : null}
              </Grid>

              <Grid md={4} item>
                <TextField
                  label={t("text.EnterStateShortName")}
                  value={formik.values.state_shortname}
                  name="state_shortname"
                  id="state_shortname"
                  placeholder={t("text.EnterStateShortName")}
                  size="small"
                  fullWidth
                  style={{ backgroundColor: "white" }}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  disabled={isSubmitting}
                  inputProps={{ 
                    maxLength: 10,
                    pattern: "[a-zA-Z\\-]*"
                  }}
                  error={formik.touched.state_shortname && Boolean(formik.errors.state_shortname)}
                />
                {formik.touched.state_shortname && formik.errors.state_shortname ? (
                  <div style={{ color: "red", margin: "5px" }}>
                    {formik.errors.state_shortname}
                  </div>
                ) : null}
              </Grid>

              <Grid item lg={6} sm={6} xs={12}>
                <Grid>
                  <Button
                    type="submit"
                    fullWidth
                    style={{
                      backgroundColor: `var(--header-background)`,
                      color: "white",
                      marginTop: "10px",
                    }}
                    disabled={isSubmitting || !formik.isValid}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {isSubmitting ? t("text.submitting") : t("text.save")}
                  </Button>
                </Grid>
              </Grid>
              <Grid item lg={6} sm={6} xs={12}>
                <Button
                  type="reset"
                  fullWidth
                  style={{
                    backgroundColor: "#F43F5E",
                    color: "white",
                    marginTop: "10px",
                  }}
                  disabled={isSubmitting}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    formik.resetForm();
                  }}
                >
                  {t("text.reset")}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </div>
    </div>
  );
};

export default StateMasterAdd;